import * as React from 'react';
import {CSSProperties} from 'react';
import {Button, Icon, Modal, Table} from 'antd';
import {simpleColumn} from '../utils/table-utils';
import locales from '../locales';
import {groupNumber, renderPriceColumn, withCurrency} from '../utils/number-utils';
import {ColumnProps, TableRowSelection} from 'antd/lib/table';
import {renderStateElementCondensed} from '../common/state';
import * as dateUtils from '../utils/date-utils';
import {getAmountTypeLabel, getAmountUnitForAmountType} from '../common/AmountType';
import ButtonGroup from 'antd/lib/button/button-group';

import './ClientRentalSummaryTable.css';
import ButtonBar from '../common/ButtonBar';
import {VisibilityProperty} from 'csstype';
import {CheckboxProps} from 'antd/lib/checkbox';
import * as paths from '../paths';
import {Link} from 'react-router-dom';
import InvoiceCell from '../invoicing/InvoiceCell';
import accountProvider from '../account/AccountProvider';
import {
  ClientRentalSummary,
  ClientRentalSummaryGridResponse,
  ClientRentalSummaryGridSection
} from "../api/dtos/summaries";
import {renderItemName} from "../item/item-name-renderer";

const addressSpanStyle: CSSProperties = {fontSize: 12, color: 'var(--color-medium-gray)'};

interface Props {
  data?: ClientRentalSummaryGridResponse;
  onViewRentalClick: (rentalId: number) => void;
  onViewReturnalClick: (returnalId: number) => void;
  onEditSummaryClick: (record: ClientRentalSummary) => void;
  onViewCommentsClick: (record: ClientRentalSummary) => void;
  onFinalizeSummaries: (items: ClientRentalSummary[]) => void;
  onRevertFinalizedSummary?: (item: ClientRentalSummary) => void;
  onCreateInvoice: (items: ClientRentalSummary[]) => void;
  onExportClick: () => void;
}

interface State {
  selectedItems: { [sectionKey: string]: ClientRentalSummary[] };
}

export const ACTION_CELL_WIDTH = 144;
export const INVOICE_CELL_WIDTH = 120;
export const REMINDERS_CELL_WIDTH = 38;

class ClientRentalSummaryTable extends React.Component<Props, State> {

  public state: State = {
    selectedItems: {}
  };

  public render(): React.ReactNode {
    const {data} = this.props;
    const {selectedItems} = this.state;
    const sections = data ? data.sections : [];

    const columns: Array<ColumnProps<ClientRentalSummaryGridSection>> = [
      {...simpleColumn('', this.renderSiteColumn), key: 'site',},
      {...simpleColumn('', this.renderSiteSummaryColumn), key: 'siteSummary'}
    ];

    const expandIconFn = () => null;
    const expandedRowKeys = sections.map(this.sectionRowKeyFn);
    const invoicingButtonsVisibility: VisibilityProperty = Object.keys(selectedItems).length > 0 ? 'visible' : 'hidden';

    return (
      <>
        <ButtonBar style={{marginBottom: 8}}>
          <Button type="primary" size="small" icon="lock"
                  style={{visibility: invoicingButtonsVisibility}}
                  onClick={this.onFinalizeButtonClick}
          >
            {locales.finalize}
          </Button>

          {accountProvider.isAdmin() && <Button type="primary" size="small" icon="file-text"
                  style={{visibility: invoicingButtonsVisibility}}
                  onClick={this.onCreateInvoiceButtonClick}
          >
            {locales.invoice_create}
          </Button>}

          <Button size="small" icon="windows"
                  onClick={this.props.onExportClick}
          >
            {locales.excel_export}
          </Button>
        </ButtonBar>

        <div className="summary-expandable-grid-scroll-container">
          <div className="summary-expandable-grid-wrapper">

            <Table
              className="summary-expandable-grid"
              dataSource={sections}
              expandedRowRender={this.expandedRowRenderer}
              expandIcon={expandIconFn}
              expandedRowKeys={expandedRowKeys}
              columns={columns}
              rowKey={this.sectionRowKeyFn}
              expandIconAsCell={false}
              size="small"
              showHeader={false}
              pagination={false}
            />

            {data && (
              <FooterTotalCell count={data.count} total={data.total}/>
            )}
          </div>
        </div>
      </>
    );
  }

  private sectionRowKeyFn = (r: ClientRentalSummaryGridSection) => `section-${r.siteSummary.site ? r.siteSummary.site.id : 'null'}`;

  private rowKeyFn = (r: ClientRentalSummary) => `item-${r.rentalItem.id}`;

  private expandedRowRenderer = (record: ClientRentalSummaryGridSection): React.ReactNode => {

    const columns: Array<ColumnProps<ClientRentalSummary>> = [
      {
        width: 38,
        ...simpleColumn('', r => renderStateElementCondensed(r.rentalItem.state)),
        key: 'state',
      }, {
        width: 100,
        ...simpleColumn(locales.rental_created, r => dateUtils.formatDateTimeOrNull(r.rental.date)),
      }, {
        width: 100,
        ...simpleColumn(locales.return, r => r.returnal && dateUtils.formatDateTimeOrNull(r.returnal.date)),
      }, {
        ...simpleColumn(locales.item, r => <Link
          to={paths.viewItemPath(r.rentalItem.item.id)}>{renderItemName(r.rentalItem.item)}</Link>),
      }, {
        width: 100,
        ...simpleColumn(locales.inventoryNumber, r => r.rentalItem.item.inventoryNumber),
      }, {
        width: 100,
        ...simpleColumn(locales.net_price, this.renderPriceColumn),
        className: 'align-right'
      }, {
        width: 100,
        ...simpleColumn(locales.settlement, this.renderAmountTypeColumn),
      }, {
        width: 100,
        ...simpleColumn(locales.amount, this.renderAmountColumn),
        className: 'align-right'
      }, {
        width: 120,
        ...simpleColumn(locales.total, this.renderTotalColumn),
        className: 'align-right'
      }, {
        width: INVOICE_CELL_WIDTH,
        ...simpleColumn(locales.invoice, this.renderInvoiceColumn),
      }, {
        width: REMINDERS_CELL_WIDTH,
        ...simpleColumn('', this.renderTodoCell),
      },
      {key: 'actions', width: ACTION_CELL_WIDTH, render: this.renderActionCell},
    ];

    const sectionKey = this.sectionRowKeyFn(record);
    return (
      <Table
        className="summary-inner-grid"
        dataSource={record.summaries}
        columns={columns}
        rowKey={this.rowKeyFn}
        bordered={true}
        size="small"
        pagination={false}
        rowSelection={this.rowSelection(sectionKey)}
      />
    );
  };

  private renderSiteColumn = (r: ClientRentalSummaryGridSection): React.ReactNode => {
    if (!r.siteSummary.site) {
      return <b><i>{locales.site_missing}</i></b>;
    }
    const {address, name} = r.siteSummary.site;
    return (
      <>
        <b>{address}</b>
        {name && <span style={addressSpanStyle}> - {name}</span>}
      </>
    );
  };

  private renderSiteSummaryColumn = (r: ClientRentalSummaryGridSection): React.ReactNode => {
    return (
      <SectionTotalCell count={r.siteSummary.count} total={r.siteSummary.total}/>
    );
  };

  private renderPriceColumn = (r: ClientRentalSummary): React.ReactNode => {
    if (r.override && r.override.price !== r.rentalItem.price) {
      return this.renderOverrideCell(
        renderPriceColumn(r.rentalItem.price),
        renderPriceColumn(r.override.price)
      );
    } else {
      return renderPriceColumn(r.rentalItem.price);
    }
  };

  private renderOverrideCell = (original: React.ReactNode, override: React.ReactNode): React.ReactNode => {
    return (
      <>
        <del style={{display: 'block', fontSize: 12}}>{original}</del>
        {override}
      </>
    );
  };

  private renderAmountTypeColumn = (r: ClientRentalSummary): React.ReactNode => {
    if (r.override && r.override.amountType !== r.rentalItem.amountType) {
      return this.renderOverrideCell(
        getAmountTypeLabel(r.rentalItem.amountType),
        getAmountTypeLabel(r.override.amountType)
      );
    } else {
      return getAmountTypeLabel(r.rentalItem.amountType);
    }
  };

  private renderAmountColumn = (r: ClientRentalSummary): React.ReactNode => {
    const {amount, rentalItem: {amountType}, override} = r;
    const dimension = getAmountUnitForAmountType(amountType);
    const overrideDimension = override && getAmountUnitForAmountType(override.amountType);
    const amountRow = amount !== null && <div style={{textAlign: 'right'}}>{`${amount} ${dimension}`}</div>;

    if (override && (override.amount !== amount || overrideDimension !== dimension)) {
      const overrideRow = <div style={{textAlign: 'right'}}>{`${override.amount} ${overrideDimension}`}</div>;
      return this.renderOverrideCell(amountRow, overrideRow);
    } else {
      return amountRow;
    }
  };

  private renderTotalColumn = (r: ClientRentalSummary): React.ReactNode => {
    if (r.override && r.override.total !== r.total) {
      return this.renderOverrideCell(
        renderPriceColumn(r.total),
        renderPriceColumn(r.override.total)
      );
    } else {
      return renderPriceColumn(r.total)
    }
  };

  private renderInvoiceColumn = (r: ClientRentalSummary): React.ReactNode => {
    return (
      <InvoiceCell
        finalized={r.finalized}
        invoice={r.invoice}
        onRevertFinalized={this.props.onRevertFinalizedSummary
          ? () => this.props.onRevertFinalizedSummary!(r)
          : undefined}
      />
    );
  };

  private renderTodoCell = (r: ClientRentalSummary): React.ReactNode => {
    if (!r.todo) {
      return null;
    }
    const hasUnchecked = r.todo.entries.some(ent => !ent.checked);
    const iconType = hasUnchecked ? 'warning' : 'check';
    const tooltip = hasUnchecked ? locales.reminders_remaining : locales.reminders_all_checked;
    return (
      <div style={{textAlign: 'center'}} title={tooltip}>
        <Icon type={iconType} style={{fontSize: 16}}/>
      </div>
    );
  };

  private renderActionCell = (text: any, record: ClientRentalSummary) => {
    const onEditClick = () => this.props.onEditSummaryClick(record);
    const viewRentalClick = () => this.props.onViewRentalClick(record.rental.id);
    const viewReturnalClick = () => {
      if (record.returnal) {
        this.props.onViewReturnalClick(record.returnal.id);
      }
    };
    const viewCommentsClick = () => {
      this.props.onViewCommentsClick(record);
    };
    const editTitle = record.finalized ? locales.finalized : locales.override_values;

    return (
      <div className="action-column" style={{textAlign: 'right', whiteSpace: 'nowrap'}}>
        <ButtonGroup>
          {record.returnal &&
              <Button icon="download" onClick={viewReturnalClick} title={locales.view_returnal}/>}
          <Button title={locales.view_rental} icon="upload" onClick={viewRentalClick}/>
          <Button icon="bars" onClick={viewCommentsClick} title={locales.reminders}/>
          <Button icon="edit" onClick={onEditClick} title={editTitle} disabled={record.finalized}/>
        </ButtonGroup>
      </div>
    );
  };

  private getSelectedItemsFlattened = (): ClientRentalSummary[] => new Array<ClientRentalSummary>()
    .concat(...Object.values(this.state.selectedItems));

  private rowSelection = (sectionKey: string): TableRowSelection<ClientRentalSummary> => {
    const selectedRowKeys = this.getSelectedItemsFlattened()
      .map(ent => this.rowKeyFn(ent));

    return {
      selectedRowKeys,
      onChange: (selectedKeys, selected) => {
        const selectedItems = {
          ...this.state.selectedItems,
          [sectionKey]: selected,
        };
        if (selected.length === 0) {
          delete selectedItems[sectionKey];
        }
        this.setState({selectedItems});
      },
      getCheckboxProps: (record: ClientRentalSummary): CheckboxProps => ({
        disabled: record.finalized,
      }),
    };
  };

  private clearSelection = () => {
    this.setState({
      selectedItems: {},
    });
  };

  private onFinalizeButtonClick = () => {
    Modal.confirm({
      title: locales.finalize_confirm,
      onOk: () => {
        this.clearSelection();
        this.props.onFinalizeSummaries(this.getSelectedItemsFlattened());
      },
      onCancel: () => {
        this.clearSelection();
      },
      okText: locales.yes,
      cancelText: locales.cancel,
    });
  };

  private onCreateInvoiceButtonClick = () => {
    if (!accountProvider.isAdmin()) {
      return;
    }
    this.clearSelection();
    this.props.onCreateInvoice(this.getSelectedItemsFlattened());
  };
}

export default ClientRentalSummaryTable;

export interface TotalProps {
  count: number;
  total: number;
}

export const SectionTotalCell = ({count, total}: TotalProps) => {
  const width = ACTION_CELL_WIDTH + INVOICE_CELL_WIDTH + REMINDERS_CELL_WIDTH - 6;
  return (
    <div className="total-footer">
      <div style={{textAlign: 'right'}}>
        <b>{withCurrency(groupNumber(total))}</b>
      </div>
      <div style={{width}}/>
    </div>
  );
};

export const FooterTotalCell = ({count, total}: TotalProps) => {
  const width = ACTION_CELL_WIDTH + INVOICE_CELL_WIDTH + REMINDERS_CELL_WIDTH + 4;
  return (
    <div className="total-footer">
      <div style={{textAlign: 'right'}}>
        {locales.total} <b>{count}</b> {locales.entry}<br/>
        <b>{withCurrency(groupNumber(total))}</b>
      </div>
      <div style={{width}}/>
    </div>
  );
};



// WEBPACK FOOTER //
// ./src/summary/ClientRentalSummaryTable.tsx